import Box from '@mui/material/Box';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { OpenPosition } from '../../api';
import { useLanguage } from '../../app/i18n';
import { NUMERIC_FONT } from '../../app/theme';
import { formatDecimal } from '../positions/formatters';

// Between the instrument and the size, as on mockup 8 ("Buy XAUUSD · 0.12 lot"). A constant, not a text.
const SEPARATOR = ' · ';

/** Props of {@link PositionLabel}. */
export interface PositionLabelProps {
  /** Buy or sell. */
  readonly side: OpenPosition['side'];
  /** The instrument, as the broker names it. */
  readonly symbol: string;
  /** The size in lots, as the API sends it (an exact decimal string). */
  readonly lots: string;
}

/**
 * One position as E10 names it, in the sheet and in the summary (M4 R4): side, instrument and size,
 * the size set in NUMERIC_FONT like every number (master UI rule; E8 does the same).
 */
export function PositionLabel({ side, symbol, lots }: PositionLabelProps): ReactElement {
  const { t } = useTranslation();
  const language = useLanguage();
  return (
    <span>
      {t('emergency.positionName', { side: t(`emergency.side.${side}`), symbol })}
      {SEPARATOR}
      <Box component="span" sx={{ fontFamily: NUMERIC_FONT }}>
        {t('emergency.lots', { lots: formatDecimal(lots, language, 2) })}
      </Box>
    </span>
  );
}
